import React, { useEffect, useRef } from 'react';

// GSAP and plugins are loaded from the CDN in index.html
declare const gsap: any;
declare const ScrollTrigger: any;

const processSteps = [
  {
    step: '01',
    title: 'Import Equipment',
    description: 'Easily add your inventory using our bulk import tools (Excel/CSV) or add items one by one. Generate and attach QR codes in minutes.',
  },
  {
    step: '02',
    title: 'Track & Inspect',
    description: 'Use the mobile app to scan QR codes, perform digital inspections, and update equipment status in real-time from the field.',
  },
  {
    step: '03',
    title: 'Assign & Operate',
    description: 'Assign tools and PPE to users and projects. Our system maintains a complete logbook of every action for a full audit trail.',
  },
  {
    step: '04',
    title: 'Report & Comply',
    description: 'Leverage powerful reporting tools to monitor compliance, track asset utilization, and ensure you\'re always audit-ready.',
  },
  {
    step: '05',
    title: 'Analyze & Optimize',
    description: 'Use historical data and analytics to optimize equipment lifecycle, predict maintenance needs, and improve overall operational safety.',
  }
];

const Process: React.FC = () => {
  const sectionRef = useRef(null);
  const timelineProgressRef = useRef(null);
  const timelineContainerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !gsap || !ScrollTrigger) return;

    gsap.registerPlugin(ScrollTrigger);

    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const items = gsap.utils.toArray('.process-card-wrapper') as HTMLElement[];
      const lastItem = items[items.length - 1];

      items.forEach((item, index) => {
        const cardContent = item.querySelector('.process-card') as HTMLElement;
        if (!cardContent) return;

        const direction = index % 2 === 0 ? -1 : 1; // -1 for left, 1 for right
        gsap.from(cardContent, {
          x: 100 * direction,
          opacity: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          }
        });
      });

      // Animate the timeline progress
      if (timelineProgressRef.current && timelineContainerRef.current && lastItem) {
        gsap.to(timelineProgressRef.current, {
          height: '100%',
          ease: 'none',
          scrollTrigger: {
            trigger: timelineContainerRef.current,
            start: 'top center',
            endTrigger: lastItem,
            end: 'center center',
            scrub: true,
          }
        });
      }
    });

    return () => {
        mm.revert();
    };
  }, []);

  return (
    <section id="process" ref={sectionRef} className="py-24 bg-gray-100 dark:bg-black/20 overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">How Shield Works</h2>
          <p className="text-lg text-gray-700 dark:text-gray-400">
            Our streamlined process makes it easy to gain full control of your equipment from day one.
          </p>
        </div>

        <div ref={timelineContainerRef} className="relative max-w-5xl lg:max-w-6xl mx-auto">
          {/* Vertical line for desktop */}
          <div className="absolute left-1/2 top-5 h-[calc(100%-2.5rem)] w-0.5 bg-gray-300 dark:bg-gray-700/50 -translate-x-1/2 hidden md:block" aria-hidden="true">
            <div ref={timelineProgressRef} className="h-full w-full bg-orange-500 dark:bg-[#FF4D00]" style={{ height: 0, boxShadow: '0 0 8px #FF4D00, 0 0 12px #FF4D00' }}></div>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {processSteps.map((step, index) => (
              <div key={index} className="relative process-card-wrapper">
                <div className={`md:flex items-center w-full ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                  
                  {/* Content Card */}
                  <div className="md:w-5/12 w-full process-card opacity-0 md:opacity-100">
                    <div className="bg-white dark:bg-black/30 p-6 rounded-lg shadow-lg shadow-orange-600/10 dark:shadow-2xl dark:shadow-[#FF4D00]/20 border border-gray-200 dark:border-white/10">
                      <div className="flex items-center md:hidden mb-3">
                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-orange-600 dark:bg-[#FF4D00]">
                          <span className="font-bold text-sm text-white dark:text-black">{step.step.replace('0', '')}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white ml-4">{step.title}</h3>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 hidden md:block">{step.title}</h3>
                      <p className="text-gray-700 dark:text-gray-400">{step.description}</p>
                    </div>
                  </div>

                  {/* Desktop Spacers */}
                  <div className="md:w-2/12 hidden md:block"></div>
                  <div className="md:w-5/12 hidden md:block"></div>
                </div>

                {/* Desktop Step Circle */}
                <div className="absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-orange-600 dark:bg-[#FF4D00] ring-8 ring-gray-100 dark:ring-black/20">
                  <span className="font-bold text-white dark:text-black">{step.step.replace('0', '')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;